import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';

interface QrScannerProps {
  onResult: (text: string) => void;
  disabled?: boolean;
}

// Camera QR reader for the admin ticket-admission desk. Uses jsQR (a few kB) over
// the browser camera. jsQR is dynamically imported so it only loads when door
// staff actually start the camera — unit tests that use manual entry never pull
// it in, and it stays out of the initial bundle.
export function QrScanner({ onResult, disabled }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastScan = useRef<{ text: string; at: number }>({ text: '', at: 0 });
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function teardown() {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }

  // Release the camera if the component unmounts mid-scan.
  useEffect(() => teardown, []);

  async function start() {
    setError(null);
    try {
      const [{ default: jsQR }, stream] = await Promise.all([
        import('jsqr'),
        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } }),
      ]);
      streamRef.current = stream;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d', { willReadFrequently: true });
      if (!video || !canvas || !ctx) {
        teardown();
        return;
      }
      video.srcObject = stream;
      await video.play();
      setScanning(true);

      const tick = () => {
        if (!streamRef.current) return;
        if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth > 0) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(image.data, image.width, image.height, {
            inversionAttempts: 'dontInvert',
          });
          if (code?.data) {
            const text = code.data;
            const now = Date.now();
            // The decode loop runs per frame — debounce repeats of the same code.
            const isRepeat = text === lastScan.current.text && now - lastScan.current.at < 2500;
            if (!isRepeat) {
              lastScan.current = { text, at: now };
              onResult(text);
            }
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      teardown();
      setError(
        'Could not start the camera. Check browser permissions, or enter the code manually below.',
      );
      setScanning(false);
    }
  }

  function stop() {
    teardown();
    setScanning(false);
  }

  return (
    <div className="space-y-3">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={scanning ? stop : () => void start()}
      >
        {scanning ? 'Stop camera' : 'Use camera'}
      </Button>

      <video
        ref={videoRef}
        aria-label="Ticket QR camera preview"
        muted
        playsInline
        className={
          scanning
            ? 'aspect-video w-full max-w-sm rounded-[var(--radius-md)] border border-[var(--color-border)] bg-black'
            : 'hidden'
        }
      >
        <track kind="captions" />
      </video>
      <canvas ref={canvasRef} className="hidden" />

      {scanning && (
        <p aria-live="polite" className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          Point the camera at the ticket QR code.
        </p>
      )}
      {error && (
        <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
          {error}
        </p>
      )}
    </div>
  );
}
